import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  Asterisk,
  Check,
  ChevronRight,
  CircleDot,
  CreditCard,
  FlaskConical,
  Infinity as InfinityIcon,
  Info,
  MapPin,
  Sparkles,
  Tag,
  User,
  Waves,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import type { PoolFormInput } from '../api/pools';
import { createPool, getPool, updatePool } from '../api/pools';
import AppButton from '../components/common/AppButton';
import AppFormField from '../components/common/AppFormField';
import AppLayout from '../components/layout/AppLayout';
import PoolIcon from '../components/layout/PoolIcon';
import PoolCustomerPicker from '../components/pools/PoolCustomerPicker';
import PoolStatusBadge from '../components/pools/PoolStatusBadge';
import { useAuth } from '../context/AuthContext';
import { useCustomerCacheContext } from '../context/CustomerCacheContext';
import type { CustomerCacheEntry } from '../types/customer';
import type { BillingModelType, Pool } from '../types/pool';
import { PoolStyle, PoolType } from '../types/pool';

type FormValues = {
  poolName: string;
  poolStyle: PoolStyle | '';
  poolType: PoolType | '';
  poolVolume: string;
  poolSize: string;
  poolStreetAddress: string;
  poolCity: string;
  poolState: string;
  customer: CustomerCacheEntry | null;
  billingType: BillingModelType;
  monthlyRate: string;
  cleaningFee: string;
  maintenanceFee: string;
};

const EMPTY_FORM: FormValues = {
  poolName: '',
  poolStyle: '',
  poolType: '',
  poolVolume: '',
  poolSize: '',
  poolStreetAddress: '',
  poolCity: '',
  poolState: '',
  customer: null,
  billingType: 'all-inclusive',
  monthlyRate: '',
  cleaningFee: '',
  maintenanceFee: '',
};

const NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

const poolFormSchema = yup.object({
  poolName: yup.string().trim().required('Pool name is required.'),
  poolStyle: yup
    .mixed<PoolStyle>()
    .oneOf(Object.values(PoolStyle), 'Select a pool style.')
    .required('Select a pool style.'),
  poolType: yup
    .mixed<PoolType>()
    .oneOf(Object.values(PoolType), 'Select a sanitation type.')
    .required('Select a sanitation type.'),
  poolVolume: yup
    .string()
    .trim()
    .required('Pool volume is required.')
    .matches(NUMERIC_PATTERN, 'Enter a number in gallons (digits only).'),
  poolSize: yup.string().trim().default(''),
  poolStreetAddress: yup
    .string()
    .trim()
    .required('Street address is required.'),
  poolCity: yup.string().trim().required('City is required.'),
  poolState: yup.string().trim().required('State is required.'),
  customer: yup
    .mixed<CustomerCacheEntry>()
    .nullable()
    .required('Select a customer for this pool.'),
  billingType: yup
    .mixed<BillingModelType>()
    .oneOf(['all-inclusive', 'pay-per-visit'])
    .required(),
  monthlyRate: yup
    .string()
    .default('')
    .when('billingType', {
      is: 'all-inclusive',
      then: (schema) =>
        schema
          .trim()
          .required('Enter the monthly rate.')
          .matches(NUMERIC_PATTERN, 'Enter a valid dollar amount.'),
    }),
  cleaningFee: yup
    .string()
    .default('')
    .when('billingType', {
      is: 'pay-per-visit',
      then: (schema) =>
        schema
          .trim()
          .required('Enter the cleaning fee.')
          .matches(NUMERIC_PATTERN, 'Enter a valid dollar amount.'),
    }),
  maintenanceFee: yup
    .string()
    .default('')
    .when('billingType', {
      is: 'pay-per-visit',
      then: (schema) =>
        schema
          .trim()
          .required('Enter the maintenance fee.')
          .matches(NUMERIC_PATTERN, 'Enter a valid dollar amount.'),
    }),
});

const CardSection = ({
  number,
  icon,
  title,
  subtitle,
  children,
}: {
  number: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-[20px] border border-grey3 bg-white shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
    <div className="flex items-center gap-3 rounded-t-[20px] border-b border-grey3 px-6 py-[18px]">
      <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-lg bg-cloud text-[13px] font-extrabold text-primary">
        {number}
      </span>
      <h3 className="flex items-center gap-2 text-base font-bold text-dark">
        <span className="text-primary [&>svg]:h-[18px] [&>svg]:w-[18px]">
          {icon}
        </span>
        {title}
      </h3>
      {subtitle && (
        <span className="ml-auto text-xs text-grey10">{subtitle}</span>
      )}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const FieldLabel = ({
  children,
  required,
  optional,
}: {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
}) => (
  <label className="mb-[7px] flex items-center gap-1.5 text-[13px] font-semibold text-dark">
    {children}
    {required && <span className="font-bold text-danger">*</span>}
    {optional && (
      <span className="text-xs font-medium text-grey10">(optional)</span>
    )}
  </label>
);

const FieldError = ({ message }: { message: string }) => (
  <div className="mt-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-danger">
    <AlertCircle className="h-[13px] w-[13px] flex-shrink-0" />
    <span>{message}</span>
  </div>
);

const FieldHint = ({ message }: { message: string }) => (
  <div className="mt-1.5 text-xs text-grey10">{message}</div>
);

const getErrorMessage = (
  errors: FieldErrors<FormValues>,
  field: keyof FormValues
): string | undefined => errors[field]?.message as string | undefined;

const PoolFormPage = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const isEdit = Boolean(poolId);
  const navigate = useNavigate();
  const { company, user, hasPermission } = useAuth();

  const canEditPools = hasPermission('canEditPools');
  const canViewCustomers = hasPermission('canViewCustomers');

  const {
    cacheData: customerCache,
    loading: customersLoading,
    error: customersError,
  } = useCustomerCacheContext();
  // Non-admins never subscribe to the customer cache, so treat the picker
  // as loaded with an empty list rather than stuck spinning.
  const customers = customerCache.customersList;
  const effectiveCustomersLoading = canViewCustomers && customersLoading;

  const [showBanner, setShowBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [existingPool, setExistingPool] = useState<Pool | null | undefined>(
    isEdit ? undefined : null
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    defaultValues: EMPTY_FORM,
    resolver: yupResolver(poolFormSchema),
  });
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const billingType = useWatch({ control, name: 'billingType' });
  const customer = useWatch({ control, name: 'customer' });

  // Load the existing pool when editing.
  useEffect(() => {
    if (!isEdit || !company?.id || !poolId) return;

    setExistingPool(undefined);
    setLoadError(null);

    getPool(company.id, poolId)
      .then((pool) => setExistingPool(pool))
      .catch(() => setLoadError('Failed to load this pool. Please try again.'));
  }, [isEdit, company?.id, poolId]);

  // Populate form values once the existing pool loads (edit mode).
  useEffect(() => {
    if (!existingPool) return;

    const billing = existingPool.billingModel;
    reset({
      poolName: existingPool.poolName ?? '',
      poolStyle: existingPool.poolStyle ?? '',
      poolType: existingPool.poolType ?? '',
      poolVolume:
        existingPool.poolVolume != null ? String(existingPool.poolVolume) : '',
      poolSize: existingPool.poolSize ?? '',
      poolStreetAddress: existingPool.poolStreetAddress ?? '',
      poolCity: existingPool.poolCity ?? '',
      poolState: existingPool.poolState ?? '',
      // Use the full cache entry if already loaded; the effect below
      // fills it in if the cache arrives afterwards.
      customer: existingPool.customer
        ? (customers.find((c) => c.id === existingPool.customer!.id) ?? {
            id: existingPool.customer.id,
            name: existingPool.customer.name,
            email: '',
            phone: null,
            address: null,
            totalPools: 0,
          })
        : null,
      billingType: billing?.type ?? 'all-inclusive',
      monthlyRate:
        billing?.type === 'all-inclusive' ? String(billing.monthlyRate) : '',
      cleaningFee:
        billing?.type === 'pay-per-visit' ? String(billing.cleaningFee) : '',
      maintenanceFee:
        billing?.type === 'pay-per-visit' ? String(billing.maintenanceFee) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPool]);

  // Reconcile the placeholder customer once the cache loads, in case it
  // arrives after the pool document did.
  useEffect(() => {
    if (customers.length === 0) return;
    if (!customer) return;
    const match = customers.find((c) => c.id === customer.id);
    if (match && match !== customer) {
      setValue('customer', match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  const buildInput = (data: FormValues): PoolFormInput => ({
    poolName: data.poolName.trim(),
    poolStyle: data.poolStyle as PoolStyle,
    poolType: data.poolType as PoolType,
    poolVolume: Number(data.poolVolume.trim()),
    poolSize: data.poolSize.trim(),
    poolStreetAddress: data.poolStreetAddress.trim(),
    poolCity: data.poolCity.trim(),
    poolState: data.poolState.trim().toUpperCase(),
    customer: {
      id: data.customer!.id,
      name: data.customer!.name,
    },
    billingModel:
      data.billingType === 'all-inclusive'
        ? {
            type: 'all-inclusive',
            monthlyRate: Number(data.monthlyRate.trim()),
          }
        : {
            type: 'pay-per-visit',
            cleaningFee: Number(data.cleaningFee.trim()),
            maintenanceFee: Number(data.maintenanceFee.trim()),
          },
  });

  const handleCancel = () => {
    if (saving) return;
    if (isEdit && poolId) {
      navigate(`/pools/${poolId}`);
    } else {
      navigate('/pools');
    }
  };

  const onValid = async (data: FormValues) => {
    setShowBanner(false);
    setSaveError(null);

    if (!company?.id || !user?.uid) {
      setSaveError('You must be signed in to save a pool.');
      return;
    }

    setSaving(true);
    try {
      const input = buildInput(data);
      if (isEdit && poolId) {
        const updated = await updatePool(company.id, poolId, input, user.uid);
        navigate(`/pools/${updated.id}`);
      } else {
        const created = await createPool(company.id, input, user.uid);
        navigate(`/pools/${created.id}`);
      }
    } catch {
      setSaveError(
        isEdit
          ? 'Failed to save changes. Please try again.'
          : 'Failed to create this pool. Please try again.'
      );
      setSaving(false);
    }
  };

  const onInvalid = () => {
    setShowBanner(true);
  };

  const errorCount = Object.keys(errors).length;
  const isActive = existingPool?.isActive !== false;

  // ---- Guard states ----

  if (!canEditPools) {
    return (
      <AppLayout title={isEdit ? 'Edit Pool' : 'Add Pool'}>
        <div className="rounded-[20px] border border-grey3 bg-white px-8 py-16 text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
          <h3 className="mb-2 text-xl font-extrabold text-dark">
            You don't have access to this page
          </h3>
          <p className="mx-auto mb-5 max-w-[380px] text-sm text-light">
            Only company admins can add or edit pools.
          </p>
          <AppButton onClick={() => navigate('/pools')}>
            Back to Pools
          </AppButton>
        </div>
      </AppLayout>
    );
  }

  if (isEdit && existingPool === undefined) {
    return (
      <AppLayout title="Edit Pool">
        <PoolFormSkeleton />
      </AppLayout>
    );
  }

  if (isEdit && (existingPool === null || loadError)) {
    return (
      <AppLayout title="Edit Pool">
        <div className="rounded-[20px] border border-grey3 bg-white px-8 py-16 text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
          <h3 className="mb-2 text-xl font-extrabold text-dark">
            {loadError ? 'Something went wrong' : 'Pool not found'}
          </h3>
          <p className="mx-auto mb-5 max-w-[380px] text-sm text-light">
            {loadError ??
              "This pool may have been deleted, or you don't have access to it."}
          </p>
          <AppButton onClick={() => navigate('/pools')}>
            Back to Pools
          </AppButton>
        </div>
      </AppLayout>
    );
  }

  const isPPV = billingType === 'pay-per-visit';

  return (
    <AppLayout
      headerContent={
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-medium text-light">
            <Link to="/pools" className="hover:text-primary">
              Pools
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-grey10" />
            {isEdit && existingPool ? (
              <>
                <Link
                  to={`/pools/${existingPool.id}`}
                  className="hover:text-primary"
                >
                  {existingPool.poolName}
                </Link>
                <ChevronRight className="h-3.5 w-3.5 text-grey10" />
                <span className="font-semibold text-dark">Edit</span>
              </>
            ) : (
              <span className="font-semibold text-dark">Add Pool</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-[26px] font-extrabold tracking-tight text-dark">
              {isEdit ? 'Edit Pool' : 'Add Pool'}
            </h1>
            {isEdit && <PoolStatusBadge isActive={isActive} />}
          </div>
        </div>
      }
      actions={
        <>
          <AppButton
            variant="tertiary"
            disabled={saving}
            onClick={handleCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            leftIcon={!saving ? <Check /> : undefined}
            loading={saving}
            onClick={() => void handleSubmit(onValid, onInvalid)()}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Pool'}
          </AppButton>
        </>
      }
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onValid, onInvalid)}
          className="mx-auto flex max-w-[860px] flex-col gap-[22px]"
        >
          {showBanner && errorCount > 0 && (
            <div className="flex items-start gap-2.5 rounded-2xl border border-danger/20 bg-dangerLight px-4 py-3.5 text-danger">
              <AlertCircle className="mt-px h-[18px] w-[18px] flex-shrink-0" />
              <div className="text-[13.5px] leading-snug">
                <strong className="block font-bold">
                  We couldn't save this pool
                </strong>
                <span className="font-medium opacity-90">
                  Please fix the {errorCount} highlighted field
                  {errorCount === 1 ? '' : 's'} below and try again.
                </span>
              </div>
            </div>
          )}

          {saveError && (
            <div className="flex items-start gap-2.5 rounded-2xl border border-danger/20 bg-dangerLight px-4 py-3.5 text-danger">
              <AlertCircle className="mt-px h-[18px] w-[18px] flex-shrink-0" />
              <span className="text-[13.5px] font-medium">{saveError}</span>
            </div>
          )}

          {isEdit && existingPool && (
            <div className="flex items-center gap-2.5 rounded-xl bg-cloud px-3.5 py-3 text-[12.5px] leading-relaxed text-light">
              <Info className="h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                This pool is currently{' '}
                <strong className="text-dark">
                  {isActive ? 'Active' : 'Inactive'}
                </strong>
                . To {isActive ? 'deactivate' : 'reactivate'} it, use the{' '}
                {isActive ? 'Deactivate' : 'Reactivate'} action on the pool's
                detail page — status isn't changed from this form.
              </span>
            </div>
          )}

          {/* Section 1 - Pool Details */}
          <CardSection number={1} icon={<PoolIcon />} title="Pool Details">
            <div className="grid grid-cols-1 gap-[18px_22px] sm:grid-cols-2">
              <div className="sm:col-span-2">
                <AppFormField<FormValues>
                  name="poolName"
                  label="Pool Name"
                  required
                  placeholder="e.g. Sunrise Community HOA Pool"
                />
              </div>

              <div>
                <FieldLabel required>Pool Style</FieldLabel>
                <AppFormField<FormValues>
                  name="poolStyle"
                  fieldType="radio-card"
                  options={[
                    {
                      value: PoolStyle.IN_GROUND,
                      icon: <Waves />,
                      title: 'In-ground',
                      subtitle: 'Built-in pool',
                    },
                    {
                      value: PoolStyle.ABOVE_GROUND,
                      icon: <CircleDot />,
                      title: 'Above-ground',
                      subtitle: 'Free-standing pool',
                    },
                  ]}
                />
                {getErrorMessage(errors, 'poolStyle') && (
                  <FieldError message={getErrorMessage(errors, 'poolStyle')!} />
                )}
              </div>

              <div>
                <FieldLabel required>Sanitation Type</FieldLabel>
                <AppFormField<FormValues>
                  name="poolType"
                  fieldType="radio-card"
                  options={[
                    {
                      value: PoolType.CHLORINE,
                      icon: <FlaskConical />,
                      title: 'Chlorine',
                      subtitle: 'Traditional sanitizer',
                    },
                    {
                      value: PoolType.SALT,
                      icon: <Sparkles />,
                      title: 'Salt',
                      subtitle: 'Salt chlorinator',
                    },
                  ]}
                />
                {getErrorMessage(errors, 'poolType') && (
                  <FieldError message={getErrorMessage(errors, 'poolType')!} />
                )}
              </div>

              <div>
                <AppFormField<FormValues>
                  name="poolVolume"
                  label="Pool Volume"
                  required
                  placeholder="42000"
                  inputMode="numeric"
                  suffix="gallons"
                  hint="Approximate total water volume."
                />
              </div>

              <div>
                <AppFormField<FormValues>
                  name="poolSize"
                  label="Pool Size (Optional)"
                  placeholder="e.g. 20x40"
                  hint="Dimensions in feet, free text."
                />
              </div>
            </div>
          </CardSection>

          {/* Section 2 - Location */}
          <CardSection
            number={2}
            icon={<MapPin className="h-[18px] w-[18px]" />}
            title="Location"
          >
            <div className="grid grid-cols-1 gap-[18px_22px] sm:grid-cols-2">
              <div className="sm:col-span-2">
                <AppFormField<FormValues>
                  name="poolStreetAddress"
                  label="Street Address"
                  required
                  placeholder="e.g. 1840 Coral Springs Blvd, Unit C"
                />
              </div>
              <div>
                <AppFormField<FormValues>
                  name="poolCity"
                  label="City"
                  required
                  placeholder="e.g. Coral Springs"
                />
              </div>
              <div>
                <AppFormField<FormValues>
                  name="poolState"
                  label="State"
                  required
                  placeholder="e.g. FL"
                  maxLength={2}
                />
              </div>
            </div>
          </CardSection>

          {/* Section 3 - Customer */}
          <CardSection
            number={3}
            icon={<User className="h-[18px] w-[18px]" />}
            title="Customer"
            subtitle="Link to an existing customer"
          >
            <FieldLabel required>Customer</FieldLabel>
            <Controller
              control={control}
              name="customer"
              render={({ field }) => (
                <PoolCustomerPicker
                  customers={customers}
                  loading={effectiveCustomersLoading}
                  value={field.value}
                  onChange={field.onChange}
                  error={getErrorMessage(errors, 'customer')}
                  fetchError={customersError}
                />
              )}
            />
            {getErrorMessage(errors, 'customer') ? (
              <FieldError message={getErrorMessage(errors, 'customer')!} />
            ) : (
              <FieldHint message="The customer must already exist. Create new customers from the Customers tab." />
            )}
          </CardSection>

          {/* Section 4 - Billing */}
          <CardSection
            number={4}
            icon={<CreditCard className="h-[18px] w-[18px]" />}
            title="Billing Model"
          >
            <div className="mb-[18px]">
              <FieldLabel required>Billing Type</FieldLabel>
              <AppFormField<FormValues>
                name="billingType"
                fieldType="button-group"
                options={[
                  {
                    value: 'all-inclusive',
                    label: 'All-Inclusive',
                    icon: <InfinityIcon />,
                  },
                  {
                    value: 'pay-per-visit',
                    label: 'Pay Per Visit',
                    icon: <Tag />,
                  },
                ]}
              />
              <FieldHint
                message={
                  isPPV
                    ? 'Charge separately for each cleaning and maintenance visit.'
                    : 'A single flat monthly rate covering all service.'
                }
              />
            </div>

            {isPPV ? (
              <div className="grid grid-cols-1 gap-[18px_22px] sm:grid-cols-2">
                <div>
                  <AppFormField<FormValues>
                    name="cleaningFee"
                    label="Cleaning Fee"
                    required
                    placeholder="0.00"
                    inputMode="decimal"
                    prefix="$"
                    suffix="per visit"
                  />
                </div>
                <div>
                  <AppFormField<FormValues>
                    name="maintenanceFee"
                    label="Maintenance Fee"
                    required
                    placeholder="0.00"
                    inputMode="decimal"
                    prefix="$"
                    suffix="per visit"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-[18px_22px] sm:grid-cols-2">
                <div>
                  <AppFormField<FormValues>
                    name="monthlyRate"
                    label="Monthly Rate"
                    required
                    placeholder="0.00"
                    inputMode="decimal"
                    prefix="$"
                    suffix="/ month"
                    hint="Flat monthly charge covering all visits and chemicals."
                  />
                </div>
              </div>
            )}
          </CardSection>

          {/* Form footer */}
          <div className="flex items-center justify-end gap-3 px-0.5 pt-1">
            <span className="mr-auto flex items-center gap-1.5 text-[12.5px] text-grey10">
              <Asterisk className="h-3.5 w-3.5" />
              Fields marked * are required
            </span>
            <AppButton
              type="button"
              variant="tertiary"
              disabled={saving}
              onClick={handleCancel}
            >
              Cancel
            </AppButton>
            <AppButton
              type="submit"
              leftIcon={!saving ? <Check /> : undefined}
              loading={saving}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Pool'}
            </AppButton>
          </div>
        </form>
      </FormProvider>
    </AppLayout>
  );
};

const PoolFormSkeleton = () => (
  <div className="mx-auto flex max-w-[860px] flex-col gap-[22px]">
    {[0, 1, 2, 3].map((i) => (
      <div
        key={i}
        className="rounded-[20px] border border-grey3 bg-white p-6 shadow-[0_6px_22px_rgba(0,29,47,0.05)]"
      >
        <div className="mb-4 h-5 w-40 animate-pulse rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%]" />
        <div className="h-24 w-full animate-pulse rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%]" />
      </div>
    ))}
  </div>
);

export default PoolFormPage;
